/**
 * Is an intentional logout in flight?
 *
 * Eleven production sites ask that question to suppress a second, session-expiry
 * logout while a deliberate one is still running — four in the axios error
 * handler, three in the Tauri auth wrapper, one each in the web and FHIR auth
 * bootstraps, the token-expiry tab-focus hook, and the on-prem Keycloak session
 * guard. Until now the answer came from `@rematch/loading`'s derived flag for
 * the `auth.logout` effect, which the plugin flips inside the
 * `dispatch.auth.logout()` WRAPPER. That made the answer a property of how the
 * logout was *invoked* rather than of whether one is running: call the exported
 * `logout()` directly and the flag stays `false` for the whole sequence, so
 * every suppression above silently stops firing.
 *
 * The signal lives here instead, on state the logout sequence itself owns, so
 * it is true whenever a logout is running and false otherwise regardless of the
 * caller's spelling. The Rematch flag is still derived and still correct for
 * `dispatch.auth.logout()` callers; it is retained until the effect delegates
 * are removed.
 *
 * It is a DEPTH, not a boolean, because the flag it replaces is one too.
 * `@rematch/loading` keeps a per-effect counter, increments it before the effect
 * body and decrements it after, and exposes `count > 0` — so the flag stayed
 * true until the LAST overlapping logout finished. Overlapping logouts are
 * reachable: sixteen of the twenty `dispatch.auth.logout()` sites fire without
 * consulting the signal first, and three need no unusual timing at all — a
 * double-click on the sidebar's Sign out, the idle AutoLogout firing into a
 * manual sign-out, the throttled desktop session-expiry trigger. A plain
 * boolean would be cleared by the FIRST sequence to reach its `finally` —
 * including one that returns early on a public route — leaving a still-running
 * logout invisible, and a 401 arriving in that window unsuppressed. Counting
 * keeps the timeline the eleven readers were written against.
 *
 * Two properties of this module are load-bearing and are asserted by a fixture
 * in the co-located test:
 *
 *   - It is a LEAF. Its only imports are `jotai`. `authActions.ts` (which sets
 *     the signal) sits on a documented static import cycle through `@/store`,
 *     and every auth slice so far has surfaced its mistakes as a production-only
 *     "cannot access before initialization". A module with no app imports cannot
 *     join that cycle, so a reader anywhere — including one loaded during store
 *     init — can import it without ordering risk.
 *   - It is NOT persisted. Plain in-memory atoms, never `atomWithStorage`:
 *     a logout that survives a page load would suppress the session-expiry
 *     logout forever on the next boot.
 */
import { atom, getDefaultStore, useAtomValue } from 'jotai';

/**
 * How many logout sequences are currently running. Written only through the
 * pair below.
 */
export const logoutInFlightDepthAtom = atom(0);

/**
 * The answer the eleven readers want, derived. Deriving it rather than reading
 * the depth at each site keeps `count > 0` in one place, and gives reactive
 * readers boolean-grained updates: a second logout starting under a first one
 * moves the depth without re-rendering anything, which is what subscribing to
 * the Rematch flag through `useSelector` did.
 */
const logoutInFlightAtom = atom((get) => get(logoutInFlightDepthAtom) > 0);

/** Called by the logout sequence at entry. */
export function enterLogoutInFlight(): void {
  getDefaultStore().set(logoutInFlightDepthAtom, (depth) => depth + 1);
}

/**
 * Called by the logout sequence in its `finally`, on every arm. The floor at
 * zero is for the unbalanced-exit case a later edit could introduce: without
 * it a stray exit would make the NEXT real logout read false as well, which is
 * the failure mode that hides a 401 storm rather than causing one.
 */
export function exitLogoutInFlight(): void {
  getDefaultStore().set(logoutInFlightDepthAtom, (depth) => Math.max(0, depth - 1));
}

/**
 * Imperative read, for the call sites that are not React render paths
 * (axios interceptors, effect bodies, broadcast-channel handlers).
 */
export function isLogoutInFlight(): boolean {
  return getDefaultStore().get(logoutInFlightAtom);
}

/**
 * Reactive read. Subscribers re-render on the flip, which is what the
 * `useSelector` readers this replaces relied on: a guard that skips while a
 * logout runs has to re-evaluate once it finishes.
 */
export function useIsLogoutInFlight(): boolean {
  return useAtomValue(logoutInFlightAtom);
}
